import { SpokePageSpinner } from '@doltech/core/lib/auth/SpokePageSpinner';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { DynamicPageProps } from '@doltech/routeDefinition';
import { environment } from '@doltech/core/lib/environment/environment';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

import cl from 'classnames';
import dynamic from 'next/dynamic';
import * as React from 'react';
import styled from 'styled-components';
import { openNewTab } from '@doltech/utils/lib/location';

import { LANDING_HEADER_HEIGHT } from '../constants';
import { LandingLayout, LandingLayoutContent } from '../LandingLayout';
import { LandingSideBar } from '../LandingSidebar/LandingSidebar';
import { LandingHeaderFullWidth } from '../PremadeHeader/LandingHeaderFullWidth';
import { SampleBlogHeader } from '../SampleBlogHeader/SampleBlogHeader';
import { useUserAuthentication } from '../../../Auth/hocs/withUserAuthentication';
import { LandingPromotionLoader } from '../LandingPromotionLoader';
import { LandingPromotionFreeTestBanner } from '../../../../shared/templates/LandingBlog/PromotionTypeForLandingPage/LandingPromotionFreeTestBanner';
import { JuniorPromotionBanner } from '../ui/JuniorPromotionBanner/JuniorPromotionBanner';

const SharedFooter = dynamic<any>(() =>
  import('../Footer/SharedFooter').then((mod) => mod.SharedFooter)
);

const Main = styled.div`
  .landing-content {
    flex: 1;
  }

  .section-middle-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .section-limit-container {
    display: grid;
    margin: 0 auto;
    max-width: 1080px;

    padding: 32px 16px;
    gap: 60px;
  }

  &.no-header {
    .landing-section-middle {
      padding-top: 0;
    }
  }

  ${fromScreen(768)} {
    .section-limit-container {
      gap: 120px;
      padding: 44px 24px 120px;
    }
  }

  ${fromScreen(1128)} {
    .section-limit-container {
      padding: 44px 0 120px;
    }
  }

  ${fromScreen(1194)} {
    .landing-left-sidebar {
      position: sticky;
      top: ${LANDING_HEADER_HEIGHT}px;
      .user-info {
        display: none;
      }
    }
  }
`;
export interface LandingLayoutDefaultProps extends DynamicPageProps {
  children?: React.ReactNode;
  pageProps: any;
  pageType: any;
  defaultSidebarOpen?: boolean;
  withHeader?: boolean;
  showPromotion?: boolean;
  withFooter?: boolean;
  logoColored?: boolean;
  loading?: boolean;
}

const LandingSampleLayoutWithPromotion = ({
  children,
  defaultSidebarOpen = false,
  withHeader = true,
  showPromotion = false,
  withFooter = true,
  logoColored = true,
  loading = false,
  pageProps,
  ...rest
}: LandingLayoutDefaultProps) => {
  const { isLoggedIn } = useGetAuthInfo();
  const { openLoginModal } = useUserAuthentication();
  const openCorpWeb = () => {
    openNewTab(environment.REACT_APP_DOL_CORP_WEBSITE_HOST_NAME);
  };

  return (
    <Main className={cl({ 'no-header': !withHeader, 'no-footer': !withFooter })}>
      <LandingLayout defaultSidebarOpen={defaultSidebarOpen} {...rest}>
        <JuniorPromotionBanner />
        {withHeader && (
          <LandingHeaderFullWidth logoColored={logoColored} showBanner={showPromotion} />
        )}
        <SampleBlogHeader
          pageProps={pageProps}
          actionTitle={isLoggedIn ? 'Tìm hiểu khóa học' : 'Đăng ký miễn phí ngay'}
          onActionClick={isLoggedIn ? openCorpWeb : openLoginModal}
        />

        {loading && <SpokePageSpinner />}
        {!loading && (
          <SectionLimitContainer>
            <LandingLayoutContent>
              {withHeader && (
                <LandingLayoutContent.LeftOverlaySidebar displayAsSideBar>
                  <LandingSideBar disableHeadTag />
                </LandingLayoutContent.LeftOverlaySidebar>
              )}

              <LandingLayoutContent.SectionMiddle>{children}</LandingLayoutContent.SectionMiddle>
            </LandingLayoutContent>
            <LandingPromotionLoader />
          </SectionLimitContainer>
        )}
      </LandingLayout>
      <div className="empty-block" />
      <LandingPromotionFreeTestBanner />
      {withFooter && <SharedFooter />}
    </Main>
  );
};

const SectionLimitContainer = ({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cl('section-middle-content section-limit-container', className)} {...rest}>
    {children}
  </div>
);

export default LandingSampleLayoutWithPromotion;
